/**
 * User API Validators
 *
 * Validators for user-related API requests and responses
 *
 * @module validators/user
 */

import { z } from 'zod';
import type { CreateUserRequest } from '../contracts/rest-api.contract';

/**
 * Validates CreateUserRequest input
 *
 * @param data Unknown data to validate
 * @returns Validated CreateUserRequest
 * @throws ZodError if validation fails
 */
export const CreateUserRequestValidator = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  display_name: z.string().min(1).max(255),
  role: z.enum(['USER', 'ADMIN', 'MODERATOR']).optional(),
});

export function validateCreateUserRequest(data: unknown): CreateUserRequest {
  return CreateUserRequestValidator.parse(data);
}

/**
 * Type guard for CreateUserRequest
 *
 * @param data Unknown data to check
 * @returns true if data is CreateUserRequest
 */
export function isCreateUserRequest(data: unknown): data is CreateUserRequest {
  try {
    CreateUserRequestValidator.parse(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safe parser that returns validation error details
 *
 * @param data Unknown data to parse
 * @returns { success: true; data } or { success: false; error }
 */
export function safeParseCreateUserRequest(
  data: unknown,
): { success: true; data: CreateUserRequest } | { success: false; error: z.ZodError } {
  const result = CreateUserRequestValidator.safeParse(data);
  return result;
}
